import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Topbar from '@/components/layout/Topbar';
import { api } from '@/lib/api';
import { Search, RefreshCw, ExternalLink, Filter, ShieldAlert, TrendingUp } from 'lucide-react';

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : Number(n).toFixed(d));
const iso = (s) => (s ? new Date(s).toLocaleString('es-AR', { hour12: false }) : '—');

const ESTRATEGIAS = [
  { id: 'todas', label: 'Todas' },
  { id: 'S5-A', label: 'S5-A' },
  { id: 'S5-B', label: 'S5-B' },
];
const FISCALES = [
  { id: 'todos', label: 'Todos' },
  { id: 'F1', label: 'F1' },
  { id: 'F2', label: 'F2' },
  { id: 'F3', label: 'F3' },
];
const ESTADOS = [
  { id: 'todos', label: 'Todos' },
  { id: 'esperando_jorge', label: 'Esperando Jorge' },
  { id: 'aprobada_jorge', label: 'Aprobada' },
  { id: 'cerrada', label: 'Cerrada' },
  { id: 'shadow', label: 'Shadow' },
  { id: 'rechazada_tribunal', label: 'Rech. tribunal' },
  { id: 'rechazada_comite', label: 'Rech. comité' },
  { id: 'rechazada_jorge', label: 'Rech. Jorge' },
  { id: 'rechazada_riesgo', label: 'Rech. riesgo' },
];
const PARES = [
  { id: '', label: 'Todos' },
  { id: 'BTCUSDT', label: 'BTCUSDT' },
  { id: 'ETHUSDT', label: 'ETHUSDT' },
];

const STATE_CLASS = {
  esperando_jorge: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  aprobada_jorge: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  cerrada: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  shadow: 'text-slate-300 border-slate-500/30 bg-slate-500/10',
  rechazada_tribunal: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  rechazada_comite: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  rechazada_riesgo: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  rechazada_jorge: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
};

function MetricCard({ label, value, sub, tone = 'neutral', testId }) {
  const tones = {
    neutral: 'panel',
    accent: 'panel border-[hsl(var(--brand-accent))]/40',
    critical: 'panel alarm-critical border',
    warning: 'panel alarm-warning border',
  };
  return (
    <div className={`${tones[tone] || tones.neutral} p-3`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Logs() {
  const [filters, setFilters] = useState({
    candidata_id: '',
    par: '',
    estrategia: 'todas',
    fiscal: 'todos',
    estado: 'todos',
    since: '',
    until: '',
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);

  const query = useMemo(() => {
    const p = {};
    if (filters.candidata_id.trim()) p.candidata_id = filters.candidata_id.trim();
    if (filters.par) p.par = filters.par;
    if (filters.estrategia !== 'todas') p.estrategia = filters.estrategia;
    if (filters.fiscal !== 'todos') p.fiscal = filters.fiscal;
    if (filters.estado !== 'todos') p.estado = filters.estado;
    if (filters.since) p.since = filters.since;
    if (filters.until) p.until = filters.until;
    p.page = page;
    p.page_size = 50;
    return p;
  }, [filters, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: l }, { data: m }] = await Promise.all([
        api.get('/logs', { params: query }),
        api.get('/metrics/coincidence'),
      ]);
      setRows(l.rows || []);
      setTotal(l.total || 0);
      setMetrics(m || null);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const resetFilters = () => {
    setFilters({ candidata_id: '', par: '', estrategia: 'todas', fiscal: 'todos', estado: 'todos', since: '', until: '' });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <>
      <Topbar
        title="Panel de logs"
        subtitle="auditoría de la operativa · todas las candidatas del sistema"
        right={
          <button
            data-testid="logs-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-1.5 border border-border hover:bg-muted transition-colors"
          ><RefreshCw size={14} /> Refrescar</button>
        }
      />

      <div className="px-3 sm:px-4 lg:px-6 py-4 space-y-4">
        {/* Header métrico */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="coincidence-metrics">
          <MetricCard
            label="Total candidatas"
            value={<span className="font-num">{metrics?.total_candidatas ?? '—'}</span>}
            sub={metrics ? `con juicio: ${metrics.con_juicio_retrospectivo}` : ''}
            testId="metric-total"
          />
          <MetricCard
            label="Coincidencia Jorge↔sistema"
            value={<span className="font-num">{fmt(metrics?.pct_coincidencia, 1)}%</span>}
            sub={metrics ? `sobre ${metrics.evaluables} evaluables (todas las candidatas)` : ''}
            tone="accent"
            testId="metric-coincidence"
          />
          <MetricCard
            label="Bloqueadas que Jorge habría operado"
            value={<span className="font-num">{metrics?.destacado_bloqueadas_pero_habria_operado ?? '—'}</span>}
            sub="caso crítico: sistema bloqueó y tú sí"
            tone={(metrics?.destacado_bloqueadas_pero_habria_operado || 0) > 0 ? 'critical' : 'warning'}
            testId="metric-blocked-but-would-operate"
          />
          <MetricCard
            label="Aprobadas que no habrías operado"
            value={<span className="font-num">{metrics?.aprobadas_pero_no_habria_operado ?? '—'}</span>}
            sub="caso crítico inverso"
            tone={(metrics?.aprobadas_pero_no_habria_operado || 0) > 0 ? 'warning' : 'neutral'}
            testId="metric-approved-would-not"
          />
        </section>

        {/* Métricas de shadow sim (candidatas no ejecutadas simuladas en sombra) */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shadow-metrics">
          <MetricCard
            label="Total sombras corridas"
            value={<span className="font-num">{metrics?.shadow_outcomes?.total ?? '—'}</span>}
            sub="candidatas no ejecutadas simuladas"
            testId="metric-shadow-total"
          />
          <MetricCard
            label="Habrían ganado"
            value={<span className="font-num">{metrics?.shadow_outcomes?.ganadoras ?? '—'}</span>}
            sub={metrics?.shadow_outcomes ? `${fmt(metrics.shadow_outcomes.pct_ganadoras, 1)}% del total sombra` : ''}
            tone={(metrics?.shadow_outcomes?.ganadoras || 0) > 0 ? 'warning' : 'neutral'}
            testId="metric-shadow-winners"
          />
          <MetricCard
            label="Habrían perdido"
            value={<span className="font-num">{metrics?.shadow_outcomes?.perdedoras ?? '—'}</span>}
            sub="el rechazo fue correcto"
            tone="accent"
            testId="metric-shadow-losers"
          />
          <MetricCard
            label="Acierto del tribunal"
            value={<span className="font-num">{fmt(metrics?.shadow_outcomes?.tribunal?.pct_tribunal_acierto, 1)}%</span>}
            sub={metrics?.shadow_outcomes?.tribunal
              ? `${metrics.shadow_outcomes.tribunal.vetos_que_habrian_perdido} de ${metrics.shadow_outcomes.tribunal.vetos_totales} vetos habrían perdido`
              : ''}
            tone="accent"
            testId="metric-tribunal-accuracy"
          />
        </section>

        {/* Filtros */}
        <section className="panel p-3" data-testid="logs-filters">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <Filter size={14} /> Filtros
            <button onClick={resetFilters} className="ml-auto underline text-[11px] hover:text-foreground" data-testid="logs-reset">restablecer</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 items-end">
            <div>
              <label className="text-[11px] text-muted-foreground">ID candidata</label>
              <div className="relative">
                <Search size={13} className="absolute left-2 top-2.5 text-muted-foreground" />
                <input
                  data-testid="filter-candidata-id"
                  value={filters.candidata_id}
                  onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, candidata_id: e.target.value })); }}
                  placeholder="cand_..."
                  className="w-full text-xs rounded-md border border-border bg-muted pl-7 pr-2 py-1.5 font-num"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Par</label>
              <select
                data-testid="filter-par"
                value={filters.par}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, par: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5"
              >
                {PARES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Estrategia</label>
              <select
                data-testid="filter-estrategia"
                value={filters.estrategia}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, estrategia: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5"
              >
                {ESTRATEGIAS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Fiscal disidente</label>
              <select
                data-testid="filter-fiscal"
                value={filters.fiscal}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, fiscal: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5"
              >
                {FISCALES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Estado</label>
              <select
                data-testid="filter-estado"
                value={filters.estado}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, estado: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5"
              >
                {ESTADOS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Desde</label>
              <input
                type="date"
                data-testid="filter-since"
                value={filters.since}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, since: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5 font-num"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Hasta</label>
              <input
                type="date"
                data-testid="filter-until"
                value={filters.until}
                onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, until: e.target.value })); }}
                className="w-full text-xs rounded-md border border-border bg-muted px-2 py-1.5 font-num"
              />
            </div>
          </div>
        </section>

        {/* Tabla */}
        <section className="panel overflow-hidden" data-testid="logs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium">Par</th>
                  <th className="text-left px-3 py-2 font-medium">Estrategia</th>
                  <th className="text-left px-3 py-2 font-medium">Régimen A5</th>
                  <th className="text-left px-3 py-2 font-medium">Estado</th>
                  <th className="text-left px-3 py-2 font-medium">Fiscal disidente</th>
                  <th className="text-left px-3 py-2 font-medium">Decisión Jorge</th>
                  <th className="text-right px-3 py-2 font-medium">Score</th>
                  <th className="text-right px-3 py-2 font-medium">R</th>
                  <th className="text-right px-3 py-2 font-medium">PnL USDT</th>
                  <th className="text-center px-3 py-2 font-medium">Juicio</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={12} className="text-center py-6 text-muted-foreground">Cargando…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-8 text-muted-foreground" data-testid="logs-empty">Sin resultados con estos filtros.</td></tr>
                )}
                {!loading && rows.map((r) => {
                  const stateCls = STATE_CLASS[r.estado] || 'text-slate-300 border-slate-500/30 bg-slate-500/10';
                  const juicio = r.habria_operado;
                  return (
                    <tr key={r.candidata_id} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`log-row-${r.candidata_id}`}>
                      <td className="px-3 py-2 font-num">{iso(r.ts_creacion)}</td>
                      <td className="px-3 py-2 font-num">{r.par || '—'}</td>
                      <td className="px-3 py-2">{r.estrategia || '—'}</td>
                      <td className="px-3 py-2">{r.regimen || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded border text-[10.5px] ${stateCls}`}>{r.estado}</span></td>
                      <td className="px-3 py-2 font-num">{r.fiscal_disidente || '—'}</td>
                      <td className="px-3 py-2">{r.decision_jorge || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 text-right font-num">{fmt(r.score_comite, 3)}</td>
                      <td className={`px-3 py-2 text-right font-num ${r.pnl_r > 0 ? 'pnl-pos' : r.pnl_r < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(r.pnl_r, 2)}</td>
                      <td className={`px-3 py-2 text-right font-num ${r.pnl_usdt > 0 ? 'pnl-pos' : r.pnl_usdt < 0 ? 'pnl-neg' : 'pnl-neutral'}`}>{fmt(r.pnl_usdt, 2)}</td>
                      <td className="px-3 py-2 text-center">
                        {juicio === true ? (
                          <span className="px-2 py-0.5 rounded border alarm-critical text-[10.5px]" title="Jorge habría operado">SÍ</span>
                        ) : juicio === false ? (
                          <span className="px-2 py-0.5 rounded border text-slate-300 border-slate-500/30 bg-slate-500/10 text-[10.5px]">NO</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/auditoria/${r.candidata_id}`}
                          data-testid={`log-audit-${r.candidata_id}`}
                          className="inline-flex items-center gap-1 text-[11px] rounded-md px-2 py-1 border border-border hover:border-[hsl(var(--brand-accent))] hover:text-[hsl(var(--brand-accent))] transition-colors"
                        >Ver auditoría <ExternalLink size={12} /></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
            <div>Mostrando <span className="font-num">{rows.length}</span> de <span className="font-num">{total}</span></div>
            <div className="flex items-center gap-1">
              <button
                data-testid="logs-prev"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-50"
              >←</button>
              <span>Página <span className="font-num">{page}</span> / <span className="font-num">{totalPages}</span></span>
              <button
                data-testid="logs-next"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2 py-1 rounded border border-border hover:bg-muted disabled:opacity-50"
              >→</button>
            </div>
          </div>
        </section>

        {/* Footer info */}
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <ShieldAlert size={13} className="mt-0.5" />
          <p className="leading-relaxed">
            El panel de logs incluye <span className="font-medium">todas</span> las candidatas generadas por el pipeline: aprobadas, rechazadas por tí, rechazadas por el tribunal, por el comité, por el motor de riesgo y por el régimen A5. Registrar tu juicio en cada vetada alimenta la métrica de coincidencia y captura los casos donde el sistema bloqueó pero tú sí habrías operado.
          </p>
        </div>
      </div>
    </>
  );
}
